/**
 * Ad Opportunities Tab
 *
 * Shows campaigns classified by opportunity type (Scale, Improve, Test, Pause)
 * with AI-generated assessments and next steps.
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { analysisApi } from './api'
import type { AdOpportunitiesResponse, OpportunityAd } from './types'

function formatCurrency(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`
  return `$${v.toFixed(2)}`
}

const OPP_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; icon: React.ComponentType<{ className?: string }> }> = {
  scale: { label: 'Scale', color: 'text-green-700', bg: 'bg-tf-feedback-success-bg', border: 'border-green-200', icon: Icons.TrendingUp },
  improve: { label: 'Improve', color: 'text-yellow-700', bg: 'bg-tf-feedback-warning-bg', border: 'border-yellow-200', icon: Icons.Wrench },
  test: { label: 'Test', color: 'text-blue-700', bg: 'bg-tf-feedback-info-bg', border: 'border-blue-200', icon: Icons.FlaskConical },
  pause: { label: 'Pause', color: 'text-red-700', bg: 'bg-tf-feedback-error-bg', border: 'border-red-200', icon: Icons.Pause },
}

function OpportunityCard({ ad }: { ad: OpportunityAd }) {
  const config = OPP_CONFIG[ad.opportunity_type] || OPP_CONFIG.test
  const IconComp = config.icon
  return (
    <div className={`rounded-xl border p-4 ${config.bg} ${config.border}`}>
      <div className="flex items-center gap-2 mb-2">
        <IconComp className={`w-4 h-4 ${config.color}`} />
        <span className={`text-xs font-bold uppercase ${config.color}`}>{config.label}</span>
      </div>
      <p className="text-sm font-medium text-tf-text truncate mb-2" title={ad.campaign_name}>
        {ad.campaign_name}
      </p>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <span className="text-tf-text-muted block">ROAS</span>
          <span className="font-semibold text-tf-text">{ad.roas.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">Spend</span>
          <span className="font-semibold text-tf-text">{formatCurrency(ad.spend)}</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">CTR</span>
          <span className="font-semibold text-tf-text">{ad.ctr.toFixed(2)}%</span>
        </div>
      </div>
    </div>
  )
}

interface AdOpportunitiesTabProps {
  startDate: string
  endDate: string
}

export function AdOpportunitiesTab({ startDate, endDate }: AdOpportunitiesTabProps) {
  const [data, setData] = useState<AdOpportunitiesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = useCallback(async (regenerate = false) => {
    if (regenerate) setRegenerating(true)
    else setLoading(true)
    setError(null)
    try {
      const report = await analysisApi.getOpportunitiesReport(startDate, endDate, regenerate)
      setData(report)
    } catch (err) {
      console.error('Error fetching opportunities report:', err)
      setError('Failed to load ad opportunities report')
    } finally {
      setLoading(false)
      setRegenerating(false)
    }
  }, [startDate, endDate])

  useEffect(() => { fetchReport(false) }, [fetchReport])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Icons.Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-sm text-tf-text-muted">Identifying ad opportunities...</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Icons.AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-red-600">{error || 'No data available'}</p>
        <button onClick={() => fetchReport(false)} className="mt-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Retry</button>
      </div>
    )
  }

  // Group ads by opportunity type
  const grouped: Record<string, OpportunityAd[]> = {}
  for (const ad of data.ads) {
    if (!grouped[ad.opportunity_type]) grouped[ad.opportunity_type] = []
    grouped[ad.opportunity_type].push(ad)
  }

  return (
    <div className="space-y-4">
      {/* Cache + Regenerate */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-tf-text-muted">
          {data.from_cache ? (
            <><Icons.Database className="w-3.5 h-3.5" /><span>Cached from {new Date(data.generated_at).toLocaleString()}</span></>
          ) : (
            <><Icons.Sparkles className="w-3.5 h-3.5 text-primary" /><span>Generated {new Date(data.generated_at).toLocaleString()}</span></>
          )}
        </div>
        <button onClick={() => fetchReport(true)} disabled={regenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/30 rounded-lg hover:bg-primary/15 disabled:opacity-50">
          {regenerating ? <><Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />Re-analyzing...</> : <><Icons.RefreshCw className="w-3.5 h-3.5" />Regenerate</>}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: AI Analysis */}
        <div className="w-full lg:w-2/5 flex flex-col gap-4">
          {data.report.header && (
            <div>
              <h3 className="text-base font-semibold text-tf-text mb-2">Summary</h3>
              <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 prose prose-sm prose-gray max-w-none">
                <ReactMarkdown>{data.report.header}</ReactMarkdown>
              </div>
            </div>
          )}

          {data.report.body && (
            <div>
              <h3 className="text-base font-semibold text-tf-text mb-2">Assessments</h3>
              <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 prose prose-sm prose-gray max-w-none">
                <ReactMarkdown>{data.report.body}</ReactMarkdown>
              </div>
            </div>
          )}

          {data.report.action_items && (
            <div>
              <h3 className="text-base font-semibold text-tf-text mb-2">Next Steps</h3>
              <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4 prose prose-sm prose-gray max-w-none">
                <ReactMarkdown>{data.report.action_items}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>

        {/* Right: Opportunity Cards grouped by type */}
        <div className="w-full lg:w-3/5 flex flex-col gap-5">
          {(['scale', 'improve', 'test', 'pause'] as const).map((type) => {
            const ads = grouped[type]
            if (!ads || ads.length === 0) return null
            const config = OPP_CONFIG[type]
            const IconComp = config.icon
            return (
              <div key={type}>
                <h3 className={`text-sm font-semibold mb-2 flex items-center gap-1.5 ${config.color}`}>
                  <IconComp className="w-4 h-4" />
                  {config.label} ({ads.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {ads.map((ad) => (
                    <OpportunityCard key={ad.campaign_id} ad={ad} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
